/* eslint-disable @typescript-eslint/no-explicit-any */
// Admin console controller (design admin-taskcoolV1, design/reference/Admin-Panel-v1-taskcool.html): the
// reference Component's UI state (view, range, filters, selection, drawer, modals, toast with undo) plus the data
// loaded through an AdminPort. Every visible value comes from the server; controls the server does not support yet
// answer with an honest toast instead of pretending (docs/admin-v1-inventory.md lists them).
import { nextTick, reactive, shallowReactive } from 'vue';
import { BackupsCtl, bkInitial } from './backups';
import { AdminError, type AdminInvitation, type ProjectMember, type NotifSettings, type AdminLink, type AdminMe, type AdminOverview, type AdminPort, type AdminProject, type AdminSettings, type AdminUser, type AuditEntry, type MailStatus, type NotifSummary, type Range, type StaffRole, type TrashPage, type UserDetail, type UserInvitation, type UsageSummary, type UserUsage, type UsageRange } from './ports';

export type View = 'overview' | 'users' | 'projects' | 'sharing' | 'audit' | 'changelog' | 'trash' | 'notif' | 'settings' | 'backups';
export const VIEWS: View[] = ['overview', 'users', 'projects', 'sharing', 'audit', 'changelog', 'trash', 'notif', 'settings', 'backups'];
/** The section lives in the URL (`/admin/users`, …) so a refresh or a shared link reopens it; `/admin` = overview. */
export function viewFromPath(path: string): View | null {
  const m = /^\/admin\/([a-z]+)\/?$/.exec(path);
  return m && (VIEWS as string[]).includes(m[1]!) ? (m[1] as View) : null;
}
const pathOf = (v: View): string => '/admin' + (v === 'overview' ? '' : '/' + v);

export type Gate = 'loading' | 'ready' | 'signin' | 'forbidden' | 'error' | '2fa';

export interface AdminData {
  overview: Partial<Record<Range, AdminOverview>>;
  users: AdminUser[] | null; usersMore: boolean; usersQ?: string; userInvites: UserInvitation[] | null;
  projects: AdminProject[] | null; links: AdminLink[] | null; invitations: AdminInvitation[] | null;
  audit: AuditEntry[] | null; auditNext: string | null; auditType: string;
  hiddenReleases: string[] | null; trash: TrashPage | null; settings: AdminSettings | null;
  mail: MailStatus | null; notif: NotifSummary | null;
  /** TaskCool 9.4: last 30 days per person */
  usage: UsageSummary | null;
}
/** The viewer's UTC offset in minutes (usage days and the day × hour grid follow the viewer's clock). */
const tzOffset = (): number => -new Date().getTimezoneOffset();

const EMPTY = (): AdminData => ({
  overview: {}, users: null, usersMore: false, userInvites: null, projects: null, links: null, invitations: null,
  audit: null, auditNext: null, auditType: 'all', hiddenReleases: null, trash: null, settings: null, mail: null, notif: null, usage: null,
});

/** Shown for controls whose server side does not exist yet (honest: nothing is changed). */
export const NOT_YET = 'این قابلیت هنوز در سرور فعال نشده است';

export interface AdminProps { accent: string; density: 'comfortable' | 'compact'; startView: View }
export const DEFAULT_ADMIN_PROPS: AdminProps = { accent: '#5b8def', density: 'comfortable', startView: 'overview' };

export class AdminController {
  props: AdminProps;
  port: AdminPort;
  state: any;
  private _t: ReturnType<typeof setTimeout> | undefined;
  private _r: (() => void) | undefined;
  private loading = new Map<string, Promise<void>>();
  /** ADR 0017 backups section (state in `state.bk`). */
  bk: BackupsCtl;

  constructor(port: AdminPort, props: Partial<AdminProps> = {}) {
    this.port = port;
    this.props = shallowReactive({ ...DEFAULT_ADMIN_PROPS, ...props });
    if (typeof location !== 'undefined') { const v = viewFromPath(location.pathname); if (v) this.props.startView = v; }
    this.state = reactive({
      view: this.props.startView, range: '30d' as Range, gQuery: '', uFilter: 'all', sel: {} as Record<string, boolean>,
      drawer: null as string | null, detail: null as UserDetail | null, userView: null as string | null, uu: null as UserUsage | null, du: null as UserUsage | null, uRange: 30 as UsageRange, invite: false, inviteEmail: '', inviteRole: 'member', menuFor: null as string | null,
      auditF: 'all', adminLog: false, nTab: 'events', emProv: null as string | null, smsProv: null as string | null, smtpOk: null as boolean | null,
      tplEv: 'otp', tplCh: 'sms', tpls: {} as Record<string, string[]>, testPhone: '',
      toast: null as null | { msg: string; undo?: () => void }, w: typeof window !== 'undefined' ? window.innerWidth : 1280,
      gate: 'loading' as Gate, me: null as AdminMe | null, data: EMPTY(), busy: false,
      compose: null as null | { ids: string[]; subject: string; body: string },
      transfer: null as null | { projectId: string; name: string; members: ProjectMember[] | null; pick: string | null },
      tfa: { sent: false, code: '', error: '' }, pending: [] as string[], bk: bkInitial(),
    });
    this.bk = new BackupsCtl(this);
  }

  get demo(): boolean { return this.port.demo; }

  setState(update: Record<string, any> | ((s: any) => Record<string, any> | null | undefined), cb?: () => void): void {
    const patch = typeof update === 'function' ? update(this.state) : update;
    if (patch) Object.assign(this.state, patch);
    if (cb) void nextTick(cb);
  }
  setData(patch: Partial<AdminData>): void { this.state.data = { ...this.state.data, ...patch }; }

  private _pop: (() => void) | undefined;
  componentDidMount(): void {
    document.documentElement.setAttribute('data-tp-admin', '');
    this.bk.attach();
    this._pop = () => { const v = viewFromPath(location.pathname) || 'overview'; if (v !== this.state.view) this.go(v, false); };
    window.addEventListener('popstate', this._pop);
    this._r = () => this.setState({ w: window.innerWidth });
    window.addEventListener('resize', this._r);
  }
  componentWillUnmount(): void { if (this._pop) window.removeEventListener('popstate', this._pop); if (this._r) window.removeEventListener('resize', this._r); clearTimeout(this._t); this.bk.dispose(); }

  /** Reference toast: 2.6 s, or 5 s when it offers Undo. */
  toast(msg: string, undo?: () => void): void {
    clearTimeout(this._t);
    this.setState({ toast: { msg, undo } });
    this._t = setTimeout(() => this.setState({ toast: null }), undo ? 5000 : 2600);
  }
  notYet(msg = NOT_YET): void { this.toast(msg); }

  can(p: string): boolean { return (this.state.me?.permissions || []).includes(p); }

  /** Loads /admin/me, then the current view and the sidebar counters. 401 → sign-in, 403/404 → not allowed. */
  async start(): Promise<void> {
    this.setState({ gate: 'loading' });
    try {
      const me = await this.port.me();
      if (me.two_factor?.required && !me.two_factor.verified) { this.setState({ me, gate: '2fa', tfa: { sent: false, code: '', error: '' } }); return; }
      this.setState({ me, gate: 'ready', data: EMPTY() });
      await Promise.all([this.loadView(this.state.view), this.load('overview'), this.load('invitations'), this.load('trash')]);
    } catch (e) {
      const kind = e instanceof AdminError ? e.kind : 'server';
      const noAccess = kind === 'forbidden' || (e instanceof AdminError && e.code === 'not_found');
      this.setState({ gate: kind === 'auth' ? 'signin' : noAccess ? 'forbidden' : 'error' });
    }
  }

  go(v: View, push = true): void {
    if (push && typeof history !== 'undefined' && /^\/admin(\/|$)/.test(location.pathname) && location.pathname !== pathOf(v)) history.pushState(history.state, '', pathOf(v) + location.search);
    this.setState({ view: v, menuFor: null, drawer: null, detail: null, invite: false, userView: null, uu: null });
    void this.loadView(v);
  }

  loadView(v: View): Promise<void> {
    const need: Record<View, string[]> = {
      overview: ['overview'], users: ['users', 'userInvites', 'usage'], projects: ['projects'], sharing: ['links', 'invitations'],
      audit: ['audit'], changelog: ['releases'], trash: ['trash'], notif: ['mail', 'notif', 'settings'], settings: ['settings'], backups: ['backups'],
    };
    return Promise.all(need[v].map(k => this.load(k))).then(() => undefined);
  }

  /** Fetches one dataset (deduplicated while in flight); errors surface as a toast. */
  load(k: string, force = false): Promise<void> {
    const d = this.state.data as AdminData;
    const have: Record<string, unknown> = {
      overview: d.overview[this.state.range as Range], users: d.users, userInvites: d.userInvites, projects: d.projects, links: d.links,
      invitations: d.invitations, audit: d.audit, releases: d.hiddenReleases, trash: d.trash, settings: d.settings, mail: d.mail, notif: d.notif, usage: d.usage,
    };
    if (!force && have[k] != null) return Promise.resolve();
    const key = k === 'overview' ? 'overview:' + this.state.range : k;
    const busy = this.loading.get(key); if (busy && !force) return busy;
    if (k === 'backups') { const p = this.bk.load().then(() => undefined).finally(() => this.loading.delete(key)); this.loading.set(key, p); return p; }
    const p = (async () => {
      try {
        if (k === 'overview') { const range = this.state.range as Range; const o = await this.port.overview(range); this.setData({ overview: { ...this.state.data.overview, [range]: o } }); }
        else if (k === 'users') {
          // Up to 5 pages of 200 for client-side search and filters, like the prototype's table.
          const all: AdminUser[] = []; let cursor: string | null = null, i = 0;
          do { const r = await this.port.users({ limit: 200, cursor }); all.push(...r.users); cursor = r.next_cursor; } while (cursor && ++i < 5);
          this.setData({ users: all, usersMore: !!cursor });
        } else if (k === 'userInvites') this.setData({ userInvites: (await this.port.userInvitations()).invitations });
        else if (k === 'projects') this.setData({ projects: (await this.port.projects()).projects });
        else if (k === 'links') this.setData({ links: (await this.port.links()).links });
        else if (k === 'invitations') this.setData({ invitations: (await this.port.invitations()).invitations });
        else if (k === 'audit') { const type = this.state.auditF; const r = await this.port.audit({ type, limit: 100 }); this.setData({ audit: r.entries, auditNext: r.next_cursor, auditType: type }); }
        else if (k === 'releases') this.setData({ hiddenReleases: (await this.port.releases()).hidden_builtin });
        else if (k === 'trash') this.setData({ trash: await this.port.trash() });
        else if (k === 'settings') this.setData({ settings: await this.port.settings() });
        else if (k === 'mail') this.setData({ mail: await this.port.mail() });
        else if (k === 'notif') this.setData({ notif: await this.port.notifSummary() });
        else if (k === 'usage') { const range = this.state.uRange as UsageRange; const usage = await this.port.usage(range, tzOffset()); if (this.state.uRange === range) this.setData({ usage }); }
      } catch (e) { this.fail(e); } finally { this.loading.delete(key); }
    })();
    this.loading.set(key, p);
    return p;
  }

  /** Global search: when the loaded list is truncated (more than 1000 users), ask the server instead. */
  private _q: ReturnType<typeof setTimeout> | undefined;
  searchUsers(q: string): void {
    clearTimeout(this._q);
    if (!this.state.data.usersMore && !this.state.data.usersQ) return;
    this._q = setTimeout(() => void (async () => {
      try {
        const r = await this.port.users({ q: q.trim() || undefined, limit: 200 });
        if (this.state.gQuery !== q) return;
        this.setData({ users: r.users, usersQ: q.trim() ? q : '', usersMore: q.trim() ? this.state.data.usersMore : !!r.next_cursor });
        if (!q.trim()) void this.load('users', true);
      } catch (e) { this.fail(e); }
    })(), 300);
  }

  setRange(r: Range): void { this.setState({ range: r }); void this.load('overview'); }
  setAuditFilter(f: string): void { this.setState({ auditF: f }); void this.load('audit', true); }
  async moreAudit(): Promise<void> {
    const cursor = this.state.data.auditNext; if (!cursor) return;
    try { const r = await this.port.audit({ type: this.state.auditF, limit: 100, cursor }); this.setData({ audit: [...(this.state.data.audit || []), ...r.entries], auditNext: r.next_cursor }); }
    catch (e) { this.fail(e); }
  }

  /** After a restore every loaded dataset is stale: drop them and reload the open section and the sidebar counters. */
  refreshData(): void {
    this.setData({ ...EMPTY(), overview: {} });
    void Promise.all([this.loadView(this.state.view), this.load('overview'), this.load('invitations'), this.load('trash')]);
  }

  fail(e: unknown): void {
    if (e instanceof AdminError && e.kind === 'auth') { this.setState({ gate: 'signin' }); return; }
    if (e instanceof AdminError && e.code === 'admin_2fa_required') { this.setState({ gate: '2fa', tfa: { sent: false, code: '', error: '' } }); return; }
    const msg = e instanceof AdminError && e.kind === 'forbidden' ? 'نقش شما اجازه این کار را ندارد'
      : e instanceof AdminError && e.kind === 'network' ? 'اتصال به سرور برقرار نیست؛ دوباره امتحان کنید'
      : e instanceof AdminError && e.code === 'maintenance' ? 'سایت در حالت تعمیر است؛ کمی بعد دوباره امتحان کنید'
      : e instanceof AdminError && e.kind === 'rejected' && e.message ? e.message
      : 'خطایی پیش آمد؛ دوباره امتحان کنید';
    this.toast(msg);
  }

  /** Runs a mutation, refreshes the affected datasets, then shows the toast (optionally with Undo). */
  async mutate(fn: () => Promise<unknown>, reload: string[], msg?: string, undo?: () => void): Promise<boolean> {
    try {
      await fn();
      await Promise.all(reload.map(k => this.load(k, true)));
      if (msg) this.toast(msg, undo);
      return true;
    } catch (e) { this.fail(e); return false; }
  }

  /** TaskCool 9.4 (design Admin Panel): one person's usage page inside the users view. */
  async openUsage(id: string, keep = false): Promise<void> {
    if (id.startsWith('inv:')) return;
    if (!keep) { this.setState({ userView: id, uu: null, drawer: null, detail: null }); if (typeof window !== 'undefined') window.scrollTo?.(0, 0); }
    try { const range = this.state.uRange as UsageRange; const uu = await this.port.userUsage(id, range, tzOffset()); if (this.state.userView === id && this.state.uRange === range) this.setState({ uu }); }
    catch (e) { this.fail(e); this.setState({ userView: null }); }
  }
  /** Design uRange: 1, 3, 7 or 30 days for the usage card, the usage column and a person's activity chart. */
  setURange(r: UsageRange): void {
    if (this.state.uRange === r) return;
    this.setState({ uRange: r });
    void this.load('usage', true);
    if (this.state.userView) void this.openUsage(this.state.userView, true);
    if (this.state.drawer) void this.loadDrawerUsage(this.state.drawer);
  }
  closeUsage(): void { this.setState({ userView: null, uu: null }); }

  async openUser(id: string): Promise<void> {
    this.setState({ drawer: id, detail: null, du: null });
    if (id.startsWith('inv:')) return;
    void this.loadDrawerUsage(id);
    try { const detail = await this.port.user(id); if (this.state.drawer === id) this.setState({ detail }); }
    catch (e) { this.fail(e); }
  }
  /** 9.6 (owner request): the user panel shows the person's activity charts for the selected range. */
  async loadDrawerUsage(id: string): Promise<void> {
    try { const range = this.state.uRange as UsageRange; const du = await this.port.userUsage(id, range, tzOffset()); if (this.state.drawer === id && this.state.uRange === range) this.setState({ du }); }
    catch (e) { this.fail(e); }
  }

  setRole(u: AdminUser, role: 'member' | 'support' | 'admin', label: string): void {
    void this.mutate(() => (role === 'member' ? this.port.removeStaff(u.id) : this.port.setStaffRole(u.id, role as StaffRole)), ['users'], 'نقش ' + u.name + ' به «' + label + '» تغییر کرد');
  }

  /** Suspend/unsuspend users (super admins are skipped like the reference); Undo restores the previous statuses. */
  setStatus(users: AdminUser[], status: 'active' | 'disabled', msg: string): void {
    const targets = users.filter(u => u.staff_role !== 'superadmin' && u.status !== status);
    if (!targets.length) { this.setState({ sel: {} }); this.toast(msg); return; }
    const back = status === 'active' ? 'disabled' : 'active';
    const undo = () => void this.mutate(() => Promise.all(targets.map(u => this.port.setUserStatus(u.id, back))), ['users']);
    this.setState({ sel: {} });
    void this.mutate(() => Promise.all(targets.map(u => this.port.setUserStatus(u.id, status))), ['users'], msg, undo);
  }

  sendInvite(): void {
    const email = this.state.inviteEmail.trim(), role = this.state.inviteRole;
    void (async () => {
      try {
        const r = await this.port.inviteUser(email, role);
        this.setState({ invite: false, view: 'users', uFilter: 'all' });
        await Promise.all([this.load('users', true), this.load('userInvites', true)]);
        this.toast(r && r.status === 'granted' ? 'نقش ' + email + ' به‌روز شد' : 'دعوت برای ' + email + ' ارسال شد');
      } catch (e) {
        if (e instanceof AdminError && e.code === 'already_member') { this.toast('این ایمیل قبلاً حساب دارد'); return; }
        this.fail(e);
      }
    })();
  }

  setLink(l: AdminLink, active: boolean): void {
    void (async () => {
      try {
        await this.port.setLinkActive(l.id, active);
        await this.load('links', true);
        this.toast(active ? 'لینک دوباره فعال شد' : 'لینک غیرفعال شد؛ دیگر کسی به آن دسترسی ندارد');
      } catch (e) {
        if (e instanceof AdminError && e.code === 'link_conflict') { this.toast('لینک فعال دیگری برای همین بخش وجود دارد'); return; }
        this.fail(e);
      }
    })();
  }

  testMail(): void {
    void (async () => {
      try {
        const r = await this.port.testMail();
        const to = this.state.me?.user?.email || '';
        if (r.status === 'failed') { this.setState({ smtpOk: false }); this.toast('ارسال ناموفق بود' + (r.error ? ': ' + r.error : '')); return; }
        this.setState({ smtpOk: true });
        this.toast(r.status === 'queued' ? 'ایمیل آزمایشی برای ' + to + ' در صف ارسال قرار گرفت' : 'ایمیل آزمایشی به ' + to + ' ارسال شد');
      } catch (e) { this.fail(e); }
    })();
  }

  /** Optimistic: the patch shows at once (rapid clicks build on it); the server's answer replaces it, a failure reloads. */
  saveSettings(patch: Partial<AdminSettings>, msg = 'ذخیره شد'): void {
    const cur = this.state.data.settings as AdminSettings | null;
    if (cur) this.setData({ settings: { ...cur, ...patch } });
    void this.mutate(async () => { this.setData({ settings: await this.port.saveSettings(patch) }); }, [], msg).then(ok => { if (!ok) void this.load('settings', true); });
  }

  /**
   * Irreversible actions (purge, empty trash) keep the reference's Undo: the rows disappear at once, the request is
   * sent only when the 5 s toast ends without Undo. Nothing is sent if the page closes first.
   */
  later(fn: () => Promise<unknown>, reload: string[], msg: string, ids: string[]): void {
    let cancelled = false;
    this.setState({ pending: [...this.state.pending, ...ids] });
    const drop = () => this.setState({ pending: this.state.pending.filter((x: string) => !ids.includes(x)) });
    this.toast(msg, () => { cancelled = true; drop(); });
    setTimeout(() => {
      if (cancelled) return;
      void this.mutate(fn, reload).finally(drop);
    }, 5000);
  }

  // ── 8.6: admin 2FA (email code) ──
  async send2fa(): Promise<void> {
    try { await this.port.send2fa(); this.setState({ tfa: { ...this.state.tfa, sent: true, error: '' } }); }
    catch (e) {
      const code = e instanceof AdminError ? e.code : '';
      if (code === 'two_factor_not_required') { await this.start(); return; }
      this.setState({ tfa: { ...this.state.tfa, sent: this.state.tfa.sent || code === 'rate_limited', error: code === 'rate_limited' ? 'کمی صبر کنید و دوباره درخواست کد بدهید' : 'ارسال کد ناموفق بود' } });
    }
  }
  async verify2fa(): Promise<void> {
    const code = this.state.tfa.code.replace(/[^0-9۰-۹]/g, '').replace(/[۰-۹]/g, (d: string) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
    if (code.length !== 6) { this.setState({ tfa: { ...this.state.tfa, error: 'کد ۶ رقمی را وارد کنید' } }); return; }
    try { await this.port.verify2fa(code); await this.start(); }
    catch (e) { this.setState({ tfa: { ...this.state.tfa, error: e instanceof AdminError && e.code === 'invalid_code' ? 'کد درست نیست یا منقضی شده است' : e instanceof AdminError && e.message ? e.message : 'تأیید ناموفق بود' } }); }
  }

  // ── 8.6: users ──
  deleteUser(u: AdminUser): void {
    const undo = () => void this.mutate(() => this.port.restoreUser(u.id), ['users', 'usage'], 'حساب ' + u.name + ' بازگردانده شد');
    this.setState({ drawer: null, detail: null });
    void this.mutate(() => this.port.deleteUser(u.id), ['users', 'usage'], 'حساب ' + u.name + ' حذف شد', undo);
  }
  deleteUsers(users: AdminUser[]): void {
    const targets = users.filter(u => u.staff_role !== 'superadmin');
    this.setState({ sel: {} });
    if (!targets.length) { this.toast('مدیر کل را نمی‌توان حذف کرد'); return; }
    const undo = () => void this.mutate(() => Promise.all(targets.map(u => this.port.restoreUser(u.id))), ['users', 'usage']);
    void this.mutate(() => Promise.all(targets.map(u => this.port.deleteUser(u.id))), ['users', 'usage'], targets.length.toLocaleString('fa-IR') + ' حساب حذف شد', undo);
  }
  openCompose(ids: string[]): void { this.setState({ compose: { ids, subject: '', body: '' } }); }
  sendCompose(): void {
    const c = this.state.compose; if (!c) return;
    if (!c.subject.trim() || !c.body.trim()) { this.toast('موضوع و متن ایمیل را بنویسید'); return; }
    void (async () => {
      try {
        const r = await this.port.emailUsers(c.ids, c.subject.trim(), c.body.trim());
        this.setState({ compose: null, sel: {} });
        this.toast('ایمیل برای ' + (r?.queued ?? c.ids.length).toLocaleString('fa-IR') + ' کاربر در صف ارسال قرار گرفت');
      } catch (e) { this.fail(e); }
    })();
  }

  // ── 8.6: projects ──
  async openTransfer(projectId: string, name: string): Promise<void> {
    this.setState({ transfer: { projectId, name, members: null, pick: null }, menuFor: null });
    try { const r = await this.port.projectMembers(projectId); if (this.state.transfer?.projectId === projectId) this.setState({ transfer: { ...this.state.transfer, members: r.members } }); }
    catch (e) { this.setState({ transfer: null }); this.fail(e); }
  }
  doTransfer(): void {
    const t = this.state.transfer; if (!t || !t.pick) return;
    const m = (t.members || []).find((x: ProjectMember) => x.user_id === t.pick);
    void this.mutate(() => this.port.transferProject(t.projectId, t.pick!), ['projects'], 'مالکیت «' + t.name + '» به ' + (m?.name || '') + ' منتقل شد').then(ok => { if (ok) this.setState({ transfer: null }); });
  }
  trashProject(id: string, name: string): void {
    const undo = () => void this.mutate(() => this.port.restoreTrash('project', id), ['projects', 'trash'], '«' + name + '» بازیابی شد');
    void this.mutate(() => this.port.trashProject(id), ['projects', 'trash'], '«' + name + '» به سطل زباله رفت', undo);
  }

  // ── 8.6: settings ──
  saveNotif(patch: Partial<NotifSettings>, msg?: string): void {
    const cur = this.state.data.settings?.notifications; if (!cur) return;
    const next: Partial<NotifSettings> = { ...cur, ...patch };
    delete next.available;
    if (next.lead == null) delete next.lead; // legacy reminder time stays until a lead is chosen
    this.saveSettings({ notifications: next as NotifSettings }, msg);
  }

  downloadUrl(url: string): void {
    const el = document.createElement('a'); el.href = url; el.setAttribute('download', ''); document.body.appendChild(el); el.click(); el.remove();
  }
}
